import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { PERM } from '../../auth/permissions';
import { useSubscription, useUsage, useCatalog, useCancelSubscription } from '../../services/billing';
import PaymentsHistory from './billing/PaymentsHistory';
import PendingCheckoutNotice from './billing/PendingCheckoutNotice';
import SubscribeModal, { type SubscribeIntent } from './billing/SubscribeModal';
import type { PlanId } from '../../types/billing';
import { phantomProps } from '@/config/phantom';
import { useTheme } from '@/contexts/ThemeContext';

const formatDate = (value: string | null): string | null => {
  if (!value) return null;
  return new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const titleCase = (value: string): string =>
  value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1);

export default function BillingSettings() {
  const { theme } = useTheme();
  const { activeMembership } = useAuth();

  const { data: subscription, isLoading, isError } = useSubscription();
  const { data: usage } = useUsage();
  const { data: catalog = [] } = useCatalog();
  const cancelMutation = useCancelSubscription();

  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [modalIntent, setModalIntent] = useState<SubscribeIntent | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canManageBilling = activeMembership?.perms?.includes(PERM.BILLING_MANAGE) ?? false;

  const nameOf = (id: PlanId): string => catalog.find((p) => p.id === id)?.name ?? titleCase(id);
  const planId: PlanId = subscription?.plan_id ?? 'free';
  const planName = nameOf(planId);
  const pendingCheckout = subscription?.pending_checkout ?? null;
  const hasActiveSub = Boolean(subscription?.subscription_id) && planId !== 'free';
  const cancelAtPeriodEnd = subscription?.cancel_at_period_end ?? false;

  const periodEnd = formatDate(subscription?.current_period_end ?? null);
  const trialEnd = formatDate(subscription?.trial_end_at ?? null);

  const handleCancel = async () => {
    setError(null);
    try {
      await cancelMutation.mutateAsync();
      setConfirmingCancel(false);
    } catch {
      setError('Failed to cancel the subscription. Please try again.');
    }
  };

  if (isError && !isLoading) {
    return (
      <div className="max-w-2xl">
        <h2 className="text-lg font-semibold text-text-primary mb-1">Billing</h2>
        <p className="text-sm text-text-secondary mb-6">Manage your subscription and billing</p>
        <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg p-4">
          <p className="text-sm text-error-800 dark:text-error-200">
            Failed to load billing information. Please try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-3 text-sm font-medium text-error-700 dark:text-error-300 hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <h2 className="text-lg font-semibold text-text-primary mb-1">Billing</h2>
      <p className="text-sm text-text-secondary mb-6">Manage your subscription and billing</p>

      <phantom-ui {...phantomProps(theme === 'dark', isLoading)}>
        {pendingCheckout && (
          <PendingCheckoutNotice
            planName={nameOf(pendingCheckout.plan_id)}
            currentPlanName={planName}
            canManageBilling={canManageBilling}
            onComplete={() =>
              setModalIntent({
                kind: 'subscribe',
                planId: pendingCheckout.plan_id,
                seats: pendingCheckout.seats,
              })
            }
          />
        )}

        {/* Current subscription */}
        <div className="bg-surface-primary rounded-lg p-6 border border-border-primary mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-base font-semibold text-text-primary mb-1">Current Plan</h3>
              <p className="text-2xl font-bold text-text-primary">{planName}</p>
            </div>
            {subscription && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-350">
                {titleCase(subscription.status)}
              </span>
            )}
          </div>

          <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-text-tertiary">Seats</dt>
              <dd className="text-text-primary font-medium">
                {subscription?.seats ?? usage?.seats_purchased ?? 0}
                {usage ? ` (${usage.seats_used} in use)` : ''}
              </dd>
            </div>

            {trialEnd && (
              <div>
                <dt className="text-text-tertiary">Trial ends</dt>
                <dd className="text-text-primary font-medium">{trialEnd}</dd>
              </div>
            )}

            {periodEnd && (
              <div>
                <dt className="text-text-tertiary">
                  {cancelAtPeriodEnd ? 'Access until' : 'Next billing date'}
                </dt>
                <dd className="text-text-primary font-medium">{periodEnd}</dd>
              </div>
            )}
          </dl>

          {cancelAtPeriodEnd && (
            <div className="mt-4 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg p-3">
              <p className="text-sm text-warning-800 dark:text-warning-200">
                Your subscription is set to cancel{periodEnd ? ` on ${periodEnd}` : ' at the end of the period'}.
                You keep access until then.
              </p>
            </div>
          )}

          {error && (
            <div className="mt-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg p-3">
              <p className="text-sm text-error-800 dark:text-error-200">{error}</p>
            </div>
          )}

          {/* Actions — only for an active paid subscription, gated on billing access */}
          {hasActiveSub && canManageBilling && (
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setModalIntent({ kind: 'change-seats' })}
                className="px-5 py-2 border border-border-primary rounded-full text-sm font-medium text-text-primary hover:bg-grey-50 dark:hover:bg-grey-800 transition-colors"
              >
                Change seats
              </button>

              {!cancelAtPeriodEnd &&
                (confirmingCancel ? (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-text-secondary">Cancel at period end?</span>
                    <button
                      type="button"
                      onClick={handleCancel}
                      disabled={cancelMutation.isPending}
                      className="px-5 py-2 bg-error-500 text-white rounded-full text-sm font-medium hover:bg-error-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {cancelMutation.isPending ? 'Cancelling…' : 'Yes, cancel'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmingCancel(false)}
                      disabled={cancelMutation.isPending}
                      className="px-5 py-2 border border-border-primary rounded-full text-sm font-medium text-text-primary hover:bg-grey-50 dark:hover:bg-grey-800 transition-colors"
                    >
                      Keep plan
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmingCancel(true)}
                    className="px-5 py-2 border border-error-300 dark:border-error-800 text-error-700 dark:text-error-300 rounded-full text-sm font-medium hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors"
                  >
                    Cancel subscription
                  </button>
                ))}
            </div>
          )}

          {hasActiveSub && !canManageBilling && (
            <p className="mt-6 text-sm text-text-tertiary">
              Managing this subscription needs billing access. Ask an Owner or Admin.
            </p>
          )}
        </div>

        {/* Payment history */}
        <PaymentsHistory />
      </phantom-ui>

      {modalIntent && (
        <SubscribeModal
          isOpen={modalIntent !== null}
          onClose={() => setModalIntent(null)}
          intent={modalIntent}
          subscription={subscription ?? null}
          usage={usage ?? null}
          catalog={catalog}
          onSuccess={() => setModalIntent(null)}
        />
      )}
    </div>
  );
}
